import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Upload } from "lucide-react";
import api from "../../api/client";
import LangTabs, { LANG_CODES } from "./LangTabs";

const COLORS = [
  { value: "bg-blue-50 text-blue-700", grad: "from-blue-500 to-cyan-500", label: "Bleu" },
  { value: "bg-purple-50 text-purple-700", grad: "from-purple-500 to-pink-500", label: "Violet" },
  { value: "bg-indigo-50 text-indigo-700", grad: "from-indigo-500 to-purple-500", label: "Indigo" },
  { value: "bg-emerald-50 text-emerald-700", grad: "from-emerald-500 to-teal-500", label: "Émeraude" },
  { value: "bg-teal-50 text-teal-700", grad: "from-teal-500 to-emerald-500", label: "Teal" },
  { value: "bg-rose-50 text-rose-700", grad: "from-rose-500 to-pink-500", label: "Rose" },
  { value: "bg-pink-50 text-pink-700", grad: "from-pink-500 to-rose-500", label: "Pink" },
  { value: "bg-orange-50 text-orange-700", grad: "from-orange-500 to-red-500", label: "Orange" },
];

const empty = () => ({
  name: "", specialty: "", phone: "", phoneAlt: "", email: "", schedule: "",
  appointmentUrl: "", moreUrl: "", photo: "", order: 0,
  color: COLORS[0].value, gradient: COLORS[0].grad, has_detail: false,
  registrations: [],
  i18n: { fr: blankDetail(), nl: blankDetail(), en: blankDetail(), it: blankDetail() },
});

function blankDetail() {
  return { title: "", paragraphs: [], qualifications: [], domains: [], memberships: [], notes: [] };
}

function ArrayField({ label, value, onChange }) {
  const v = Array.isArray(value) ? value : [];
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="space-y-2 mt-1">
        {v.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input type="text" value={item} onChange={(e) => { const c = [...v]; c[i] = e.target.value; onChange(c); }}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-2xl text-sm" />
            <button type="button" onClick={() => onChange(v.filter((_, j) => j !== i))} className="p-2 text-rose-600 hover:bg-rose-50 rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...v, ""])} className="text-xs font-semibold text-[#2E4DB5] hover:underline">
          + Ajouter
        </button>
      </div>
    </div>
  );
}

function ParagraphsField({ value, onChange }) {
  const v = Array.isArray(value) ? value : [];
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Paragraphes (bio)</label>
      <div className="space-y-2 mt-1">
        {v.map((item, i) => (
          <div key={i} className="flex gap-2">
            <textarea value={item} onChange={(e) => { const c = [...v]; c[i] = e.target.value; onChange(c); }} rows={3}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-2xl text-sm" />
            <button type="button" onClick={() => onChange(v.filter((_, j) => j !== i))} className="p-2 text-rose-600 hover:bg-rose-50 rounded-full self-start">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...v, ""])} className="text-xs font-semibold text-[#2E4DB5] hover:underline">
          + Ajouter paragraphe
        </button>
      </div>
    </div>
  );
}

export default function StaffAdmin() {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [lang, setLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/staff").then((r) => setStaff(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    const i18n = m.i18n || {};
    LANG_CODES.forEach((l) => { i18n[l] = i18n[l] || blankDetail(); });
    setEditing({ ...m, i18n, registrations: m.registrations || [] });
    setLang("fr"); setErr("");
  };
  const startCreate = () => { setEditing(empty()); setLang("fr"); setErr(""); };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { setErr("Photo trop grande (max 1.5 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setEditing((cur) => ({ ...cur, photo: e.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { ...editing };
      // Ensure all i18n arrays are clean (no empty strings)
      LANG_CODES.forEach((l) => {
        const d = payload.i18n[l];
        ["paragraphs", "qualifications", "domains", "memberships", "notes"].forEach((k) => {
          d[k] = (d[k] || []).filter((x) => x && x.trim() !== "");
        });
      });
      payload.registrations = (payload.registrations || []).filter((x) => x && x.trim() !== "");
      if (editing.id) {
        await api.put(`/staff/${editing.id}`, payload);
      } else {
        await api.post("/staff", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce professionnel ?")) return;
    await api.delete(`/staff/${id}`);
    load();
  };

  const setI18n = (field, val) =>
    setEditing((e) => ({ ...e, i18n: { ...e.i18n, [lang]: { ...e.i18n[lang], [field]: val } } }));

  return (
    <div data-testid="staff-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900">Professionnels</h1>
          <p className="text-slate-500 text-sm mt-1">{staff.length} membre(s)</p>
        </div>
        <button onClick={startCreate} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="staff-create-btn">
          <Plus className="w-4 h-4" /> Nouveau professionnel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {staff.map((m) => (
          <div key={m.id} className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center gap-4">
            <img src={m.photo || "/images/symbol.png"} alt="" className="w-16 h-16 rounded-xl object-cover bg-slate-100" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{m.name}</p>
              <p className="text-xs text-slate-500 truncate">{m.specialty}</p>
              {m.has_detail && <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wider bg-blue-50 text-[#2E4DB5] px-2 py-0.5 rounded-full">Page détail</span>}
            </div>
            <button onClick={() => startEdit(m)} className="p-2 rounded-full hover:bg-slate-50 text-slate-600" data-testid={`edit-staff-${m.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(m.id)} className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="staff-editor-modal">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-2xl font-bold text-slate-900">{editing.id ? "Modifier" : "Ajouter un professionnel"}</h2>
              <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-slate-50"><X className="w-5 h-5" /></button>
            </div>

            {err && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-2xl p-3">{err}</div>}

            {/* Photo upload */}
            <div className="mb-6 flex items-center gap-5">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                {editing.photo && <img src={editing.photo} alt="" className="w-full h-full object-cover" />}
              </div>
              <label className="border border-slate-200 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50 flex items-center gap-2">
                <Upload className="w-4 h-4" /> Photo (max 1.5 Mo)
                <input type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} className="hidden" data-testid="staff-photo-input" />
              </label>
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom complet</label>
                <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" data-testid="staff-name-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Spécialité (étiquette FR)</label>
                <input type="text" value={editing.specialty} onChange={(e) => setEditing({ ...editing, specialty: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Téléphone</label>
                <input type="text" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Téléphone alternatif</label>
                <input type="text" value={editing.phoneAlt || ""} onChange={(e) => setEditing({ ...editing, phoneAlt: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</label>
                <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Ordre (1 = en premier)</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Horaires (saut de ligne autorisé)</label>
                <textarea value={editing.schedule} onChange={(e) => setEditing({ ...editing, schedule: e.target.value })} rows={2}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">URL prise de RDV</label>
                <input type="text" value={editing.appointmentUrl} onChange={(e) => setEditing({ ...editing, appointmentUrl: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" placeholder="mailto:... ou https://..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">URL externe (optionnel)</label>
                <input type="text" value={editing.moreUrl || ""} onChange={(e) => setEditing({ ...editing, moreUrl: e.target.value })}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Thème couleur</label>
                <select value={editing.color} onChange={(e) => { const c = COLORS.find((x) => x.value === e.target.value); setEditing({ ...editing, color: c.value, gradient: c.grad }); }}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm">
                  {COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <input type="checkbox" id="has_detail" checked={editing.has_detail} onChange={(e) => setEditing({ ...editing, has_detail: e.target.checked })}
                  className="w-4 h-4" data-testid="staff-has-detail" />
                <label htmlFor="has_detail" className="text-sm text-slate-700">Créer une page de détail (bio, qualifications...)</label>
              </div>
            </div>

            {editing.has_detail && (
              <>
                <div className="mt-6 mb-4 border-t border-slate-100 pt-6">
                  <h3 className="font-outfit font-bold text-slate-900 mb-3">Page de détail — contenus multilingues</h3>
                  <LangTabs active={lang} onChange={setLang} />
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Titre de la spécialité ({lang.toUpperCase()})</label>
                    <input type="text" value={editing.i18n[lang].title || ""} onChange={(e) => setI18n("title", e.target.value)}
                      className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
                  </div>
                  <ParagraphsField value={editing.i18n[lang].paragraphs} onChange={(v) => setI18n("paragraphs", v)} />
                  <ArrayField label={`Qualifications (${lang.toUpperCase()})`} value={editing.i18n[lang].qualifications} onChange={(v) => setI18n("qualifications", v)} />
                  <ArrayField label={`Domaines d'intervention (${lang.toUpperCase()})`} value={editing.i18n[lang].domains} onChange={(v) => setI18n("domains", v)} />
                  <ArrayField label={`Affiliations (${lang.toUpperCase()})`} value={editing.i18n[lang].memberships} onChange={(v) => setI18n("memberships", v)} />
                  <ArrayField label={`Notes informatives (${lang.toUpperCase()})`} value={editing.i18n[lang].notes} onChange={(v) => setI18n("notes", v)} />
                </div>

                <div className="mt-6">
                  <ArrayField label="Numéros d'enregistrement (INAMI, COMPSY, etc.)" value={editing.registrations} onChange={(v) => setEditing({ ...editing, registrations: v })} />
                </div>
              </>
            )}

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-full">Annuler</button>
              <button onClick={save} disabled={saving} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60" data-testid="staff-save-btn">
                <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
